import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// The tab for a URL slug (slugs maps tab key -> slug; the default tab has none).
export function tabForSlug(slugs, slug, defaultTab) {
  const hit = Object.keys(slugs).find((k) => slugs[k] === slug);
  return hit || defaultTab;
}

// Keeps a screen's tab in its address: `base` for the default tab, otherwise
// `base/<slug>`. Changing the tab on screen replaces the address (so Back leaves
// the screen); navigating to a different tab address (a link, Back/Forward)
// calls onUrlTab(tab) so the screen can switch the way its own tab bar does.
export default function useTabInPath({ base, slugs, tab, defaultTab, onUrlTab }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const synced = useRef(pathname);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const onUrlTabRef = useRef(onUrlTab);
  onUrlTabRef.current = onUrlTab;

  useEffect(() => {
    if (pathname === synced.current) return;
    synced.current = pathname;
    const rest = pathname.startsWith(base + '/') ? pathname.slice(base.length + 1) : '';
    const urlTab = tabForSlug(slugs, rest, defaultTab);
    if (urlTab !== tabRef.current && onUrlTabRef.current) onUrlTabRef.current(urlTab);
  }, [pathname, base, slugs, defaultTab]);

  useEffect(() => {
    const want = tab === defaultTab || !slugs[tab] ? base : `${base}/${slugs[tab]}`;
    if (want !== synced.current) {
      synced.current = want;
      navigate(want, { replace: true });
    }
  }, [tab, base, slugs, defaultTab, navigate]);
}
